import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import Footer from '../components/Footer';
import { sponsorshipAPI } from '../api';

const SponsorshipListPage = () => {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('PUBLISHED');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSponsorships();
  }, [filter]);

  const fetchSponsorships = async () => {
    try {
      setLoading(true);
      const data = await sponsorshipAPI.getAllSponsorships(filter);
      setSponsorships(data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const calculateProgress = (funded, required) => {
    return Math.min((funded / required) * 100, 100);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <BrandNavbar />

      <main className="flex-grow container mx-auto px-4 py-8 mt-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Sponsorships</h1>
            <p className="text-gray-600">
              Support Creator projects and help bring creative ideas to life
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-4">
            <button
              onClick={() => setFilter('PUBLISHED')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'PUBLISHED'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Published
            </button>
            <button
              onClick={() => setFilter('FULLY_FUNDED')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'FULLY_FUNDED'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Fully Funded
            </button>
            <button
              onClick={() => setFilter(null)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All
            </button>
          </div>

          {/* Sponsorships Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading sponsorships...</p>
            </div>
          ) : sponsorships.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600">No sponsorships found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sponsorships.map((sponsorship) => (
                <div
                  key={sponsorship.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer"
                  onClick={() => navigate(`/sponsorships/${sponsorship.id}`)}
                >
                  <div className="p-6">
                    {/* Status Badge */}
                    <div className="mb-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          sponsorship.status === 'FULLY_FUNDED'
                            ? 'bg-green-100 text-green-800'
                            : sponsorship.status === 'PUBLISHED'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {sponsorship.status.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Creator */}
                    <div className="mb-3">
                      <p className="text-sm text-gray-500">
                        by <span className="font-medium text-gray-700">{sponsorship.userName}</span>
                      </p>
                    </div>

                    {/* Project Overview */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                      {sponsorship.projectOverview}
                    </h3>

                    {/* Interest Badge */}
                    {sponsorship.interest && (
                      <div className="mb-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          {sponsorship.interest}
                        </span>
                      </div>
                    )}

                    {/* Funding Progress */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(sponsorship.amountFunded)}
                        </span>
                        <span className="text-gray-600">
                          of {formatCurrency(sponsorship.amountRequired)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${calculateProgress(
                              sponsorship.amountFunded,
                              sponsorship.amountRequired
                            )}%`
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{sponsorship.investmentCount || 0} investors</span>
                      <span>
                        {formatCurrency(sponsorship.remainingAmount)} remaining
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SponsorshipListPage;
